import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { centralApi } from '@marque/api/central'
import { useEffect } from 'react'
import { AnthropicKeyCard } from '../components/AnthropicKeyCard'
import { GoogleKeysCard } from '../components/GoogleKeysCard'
import { DocumentIntelligenceKeysCard } from '../components/DocumentIntelligenceKeysCard'

const schema = z.object({
    host: z.string().min(1, 'SMTP host is required').nullable().default(null),
    port: z.coerce.number().int().min(1).max(65535).nullable().default(587),
    sslMode: z.enum(['Ssl', 'StartTls', 'StartTlsWhenPossible', 'None']).nullable().default('StartTls'),
    fromAddress: z.string().email('Must be a valid email').nullable().default(null),
    fromName: z.string().nullable().optional(),
    authMode: z.enum(['BasicAuth', 'OAuth2', 'GraphApi']).default('BasicAuth'),
    username: z.string().nullable().optional(),
    password: z.string().nullable().optional(),
    oauth2TenantId: z.string().nullable().optional(),
    oauth2ClientId: z.string().nullable().optional(),
    oauth2ClientSecret: z.string().nullable().optional(),
})
type FormValues = z.infer<typeof schema>

const SSL_MODES = [
    { value: 'StartTls', label: 'STARTTLS (recommended)' },
    { value: 'Ssl', label: 'SSL/TLS' },
    { value: 'StartTlsWhenPossible', label: 'STARTTLS when possible' },
    { value: 'None', label: 'None (plain)' },
]

const field = 'w-full rounded-md border border-neutral-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

export function SmtpSettingsPage() {
    const qc = useQueryClient()

    const { data, isLoading, isError } = useQuery({
        queryKey: ['smtp-config'],
        queryFn: centralApi.getSmtpConfig,
    })

    const { register, handleSubmit, reset, control, formState: { errors, isDirty } } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            host: null,
            port: 587,
            sslMode: 'StartTls',
            fromAddress: null,
            fromName: null,
            authMode: 'BasicAuth',
            username: null,
            password: null,
            oauth2TenantId: null,
            oauth2ClientId: null,
            oauth2ClientSecret: null,
        },
    })

    const authMode = useWatch({ control, name: 'authMode' })

    useEffect(() => {
        if (data) {
            reset({
                host: data.host,
                port: data.port ?? 587,
                sslMode: (data.sslMode as FormValues['sslMode']) ?? 'StartTls',
                fromAddress: data.fromAddress,
                fromName: data.fromName,
                authMode: (data.authMode as FormValues['authMode']) ?? 'BasicAuth',
                username: null,
                password: null,
                oauth2TenantId: data.oauth2TenantId ?? null,
                oauth2ClientId: data.oauth2ClientId ?? null,
                oauth2ClientSecret: null,
            })
        }
    }, [data, reset])

    const save = useMutation({
        mutationFn: (v: FormValues) =>
            centralApi.setSmtpConfig({
                host: v.host ?? null,
                port: v.port ?? null,
                sslMode: v.sslMode ?? null,
                fromAddress: v.fromAddress ?? null,
                fromName: v.fromName ?? null,
                authMode: v.authMode,
                username: v.authMode === 'BasicAuth' ? (v.username ?? null) : null,
                password: v.authMode === 'BasicAuth' ? (v.password ?? null) : null,
                oauth2TenantId: v.authMode !== 'BasicAuth' ? (v.oauth2TenantId ?? null) : null,
                oauth2ClientId: v.authMode !== 'BasicAuth' ? (v.oauth2ClientId ?? null) : null,
                oauth2ClientSecret: v.authMode !== 'BasicAuth' ? (v.oauth2ClientSecret ?? null) : null,
            }),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['smtp-config'] })
        },
    })

    return (
        <div className="max-w-2xl mx-auto py-8 px-4">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-neutral-900">Platform SMTP Settings</h1>
                <p className="mt-1 text-sm text-neutral-500">
                    Default email transport used for all tenants that don&apos;t configure their own.
                </p>
            </div>

            {isLoading && <p className="text-neutral-500 text-sm">Loading…</p>}
            {isError && <p className="text-red-600 text-sm">Failed to load SMTP settings.</p>}

            {!isLoading && !isError && (
                <div className="bg-white rounded-lg border border-neutral-200 p-6">
                    {data?.isConfigured && (
                        <div className="mb-5 flex items-center gap-3 p-3 rounded-md bg-emerald-50 border border-emerald-200">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0 text-emerald-600" aria-hidden>
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            <p className="text-xs text-emerald-700">
                                Platform SMTP is configured.{' '}
                                {data.authMode === 'GraphApi'
                                    ? 'Using Microsoft Graph API.'
                                    : data.authMode === 'OAuth2'
                                    ? 'Using OAuth2 SMTP bearer token.'
                                    : data.hasUsername ? 'Basic auth credentials stored.' : ''}
                            </p>
                        </div>
                    )}

                    {!data?.isConfigured && (
                        <div className="mb-5 flex items-center gap-3 p-3 rounded-md bg-amber-50 border border-amber-200">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 shrink-0 text-amber-500" aria-hidden>
                                <path d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            </svg>
                            <p className="text-xs text-amber-700">No platform SMTP configured. Emails will be held until configured.</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit(v => save.mutate(v))} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-xs font-medium text-neutral-700 mb-1">Authentication mode</label>
                                <select {...register('authMode')} className={field}>
                                    <option value="BasicAuth">Basic auth (username / password)</option>
                                    <option value="OAuth2">OAuth2 — SMTP bearer token (XOAUTH2)</option>
                                    <option value="GraphApi">Microsoft Graph API (recommended for M365)</option>
                                </select>
                            </div>

                            {authMode !== 'GraphApi' && (
                                <>
                                    <div className="col-span-2">
                                        <label className="block text-xs font-medium text-neutral-700 mb-1">SMTP host</label>
                                        <input {...register('host')} className={field} placeholder="smtp.office365.com" />
                                        {errors.host && <p className="mt-1 text-xs text-red-600">{errors.host.message}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-neutral-700 mb-1">Port</label>
                                        <input {...register('port')} type="number" min={1} max={65535} className={field} placeholder="587" />
                                        {errors.port && <p className="mt-1 text-xs text-red-600">{errors.port.message}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-neutral-700 mb-1">SSL mode</label>
                                        <select {...register('sslMode')} className={field}>
                                            {SSL_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </select>
                                    </div>
                                </>
                            )}

                            <div>
                                <label className="block text-xs font-medium text-neutral-700 mb-1">From address</label>
                                <input {...register('fromAddress')} type="email" className={field} placeholder="noreply@platform.com" />
                                {errors.fromAddress && <p className="mt-1 text-xs text-red-600">{errors.fromAddress.message}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-neutral-700 mb-1">From name (optional)</label>
                                <input {...register('fromName')} className={field} placeholder="Marque Platform" />
                            </div>

                            {authMode === 'BasicAuth' && (
                                <>
                                    <div>
                                        <label className="block text-xs font-medium text-neutral-700 mb-1">Username (optional)</label>
                                        <input {...register('username')} autoComplete="username" className={field} />
                                        {data?.hasUsername && !errors.username && (
                                            <p className="mt-1 text-xs text-neutral-500">Credentials stored — enter new values to rotate.</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-neutral-700 mb-1">Password (optional)</label>
                                        <input {...register('password')} type="password" autoComplete="new-password" className={field} />
                                    </div>
                                </>
                            )}

                            {authMode === 'GraphApi' && (
                                <div className="col-span-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-700">
                                    Register an app in Azure AD, grant the <strong>Mail.Send</strong> application permission on Microsoft Graph, then enter the credentials below.
                                    The from address above is used as the sending mailbox — no SMTP host or port needed.
                                </div>
                            )}

                            {(authMode === 'OAuth2' || authMode === 'GraphApi') && (
                                <>
                                    {authMode === 'OAuth2' && (
                                        <div className="col-span-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-700">
                                            Register an app in Azure AD, grant the <strong>SMTP.SendAsApp</strong> permission on Office 365 Exchange Online, then enter the credentials below.
                                            The from address above is used as the sending mailbox.
                                        </div>
                                    )}

                                    <div className="col-span-2">
                                        <label className="block text-xs font-medium text-neutral-700 mb-1">Azure AD tenant ID</label>
                                        <input {...register('oauth2TenantId')} className={field} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-neutral-700 mb-1">Client ID (application ID)</label>
                                        <input {...register('oauth2ClientId')} className={field} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-neutral-700 mb-1">Client secret</label>
                                        <input {...register('oauth2ClientSecret')} type="password" autoComplete="new-password" className={field} />
                                        {data?.hasOAuth2ClientSecret && (
                                            <p className="mt-1 text-xs text-neutral-500">Secret stored — enter a new value to rotate.</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        {save.isError && (
                            <p className="text-xs text-red-600">Failed to save settings. Please try again.</p>
                        )}
                        {save.isSuccess && !isDirty && (
                            <p className="text-xs text-emerald-600">Settings saved successfully.</p>
                        )}

                        <div className="flex gap-3 pt-2">
                            <button type="submit" disabled={save.isPending || !isDirty}
                                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                {save.isPending ? 'Saving…' : 'Save settings'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <AnthropicKeyCard />
            <GoogleKeysCard />
            <DocumentIntelligenceKeysCard />
        </div>
    )
}
